export const PLAN_BADGE_COLORS = [
  {
    bg: "#5B4FCF",
    text: "#FFFFFF",
    icon: "mdi-star-outline",
  },
  {
    bg: "#0A8FE8",
    text: "#FFFFFF",
    icon: "mdi-rocket",
  },
  {
    bg: "#1E8449",
    text: "#FFFFFF",
    icon: "mdi-leaf",
  },
  {
    bg: "#E67E22",
    text: "#FFFFFF",
    icon: "mdi-fire",
  },
  {
    bg: "#F4D03F",
    text: "#000000",
    icon: "mdi-diamond-stone",
  },
];

export const getPlanBadgeConfigByOrder = (order) => {
  const index = Math.max(0, Math.min(PLAN_BADGE_COLORS.length - 1, Number(order) || 0));
  return PLAN_BADGE_COLORS[index];
};

export const buildPlanColorMapFromPlans = (plans = []) => {
  const sorted = [...plans].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const map = new Map();
  sorted.forEach((plan, idx) => {
    map.set(plan.name, getPlanBadgeConfigByOrder(idx));
  });
  return map;
};

const hashPlanName = (planName) => {
  let hash = 0;
  for (let i = 0; i < planName.length; i += 1) {
    hash = planName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % PLAN_BADGE_COLORS.length;
};

export const getPlanBadgeConfigForPlan = (planName, plans = []) => {
  if (!planName) {
    return null;
  }

  if (plans.length) {
    const colorMap = buildPlanColorMapFromPlans(plans);
    return colorMap.get(planName) || getPlanBadgeConfigByOrder(hashPlanName(planName));
  }

  return getPlanBadgeConfigByOrder(hashPlanName(planName));
};

export const buildPlanEntitlements = (plan) => {
  const items = [];
  const songLimit = Number(plan.song_limit) || 0;
  const artistLimit = Number(plan.artist_limit) || 0;
  const musicLinkLimit = Number(plan.music_link_limit) || 0;
  const linkLandingPageLimit = Number(plan.link_landing_page_limit) || 0;

  if (songLimit > 0) {
    items.push(`${songLimit} songs`);
  }
  if (artistLimit > 0) {
    items.push(`${artistLimit} artists`);
  }
  if (plan.music_link_enabled && musicLinkLimit > 0) {
    items.push(`${musicLinkLimit} music links`);
  }
  if (plan.link_landing_page_enabled && linkLandingPageLimit > 0) {
    items.push(`${linkLandingPageLimit} link landing pages`);
  }

  return items;
};

export const formatSubscriptionStatus = (status) => {
  if (!status) {
    return "";
  }
  return String(status).replace(/_/g, " ");
};

export const getSubscriptionStatusBadgeConfig = (status) => {
  const normalized = String(status || "").toLowerCase().replace(/\s+/g, "_");

  const configs = {
    active: { label: "Active", className: "is-active" },
    trialing: { label: "Trialing", className: "is-active" },
    past_due: { label: "Past due", className: "is-past-due" },
    canceled: { label: "Canceled", className: "is-canceled" },
    cancelled: { label: "Canceled", className: "is-canceled" },
    inactive: { label: "Inactive", className: "is-inactive" },
    unpaid: { label: "Unpaid", className: "is-past-due" },
    incomplete: { label: "Incomplete", className: "is-inactive" },
    incomplete_expired: { label: "Expired", className: "is-canceled" },
    paused: { label: "Paused", className: "is-inactive" },
  };

  if (configs[normalized]) {
    return configs[normalized];
  }

  return {
    label: formatSubscriptionStatus(status),
    className: "is-inactive",
  };
};

export const formatSubscriptionDate = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  let date;
  if (typeof value === "number") {
    date = new Date(value > 1e12 ? value : value * 1000);
  } else {
    date = new Date(value);
  }

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const getSubscriptionPeriodEnd = (subscription) => {
  if (!subscription) {
    return null;
  }

  return (
    subscription.current_period_end
    ?? null
  );
};

export const getSubscriptionPeriodStart = (subscription) => {
  if (!subscription) {
    return null;
  }

  return (
    subscription.current_period_start
    ?? null
  );
};

export const getActiveTenantSubscription = (user) => {
  if (!user) {
    return null;
  }

  const nested = user.tenant_subscription;
  if (nested && typeof nested === "object") {
    return {
      ...nested,
      plan_name: nested.plan_name || user.tenant_subscription_plan || null,
      status: nested.status ?? user.tenant_subscription_status ?? null,
      subscription_id: getSubscriptionId(nested, user),
      current_period_end: getSubscriptionPeriodEnd(nested) ?? getSubscriptionPeriodEnd(user),
      current_period_start: getSubscriptionPeriodStart(nested) ?? getSubscriptionPeriodStart(user),
    };
  }

  if (!user.tenant_subscription_plan) {
    return null;
  }

  return {
    plan_name: user.tenant_subscription_plan,
    status: user.tenant_subscription_status || null,
    subscription_id: getSubscriptionId(null, user),
    current_period_end: getSubscriptionPeriodEnd(user),
    current_period_start: getSubscriptionPeriodStart(user),
  };
};

export const getSubscriptionId = (subscription, user = null) => {
  const candidates = [
    subscription?.subscription_id,
    subscription?.subscriptionId,
    subscription?.stripe_subscription_id,
    user?.tenant_subscription?.subscription_id,
    user?.tenant_stripe_subscription_id,
  ];

  const stripeSubscriptionId = candidates.find(
    (id) => typeof id === "string" && id.startsWith("sub_")
  );

  if (stripeSubscriptionId) {
    return stripeSubscriptionId;
  }

  return candidates.find((id) => typeof id === "string" && id.trim()) || null;
};

export const resolveCurrentPlan = (plans = [], subscription = null) => {
  if (!plans.length) {
    return null;
  }

  return (
    plans.find((plan) => plan.is_current)
    || (subscription?.plan_name
      ? plans.find((plan) => plan.name === subscription.plan_name)
      : null)
    || null
  );
};

export const userHasActiveSubscription = (plans = [], subscription = null) => {
  const currentPlan = resolveCurrentPlan(plans, subscription);
  return !!(
    currentPlan
    || subscription?.plan_name
    || subscription?.subscription_id
    || subscription?.status
  );
};

export const userHasTenantSubscriptionPlan = (user) => {
  if (!user) {
    return false;
  }

  const subscription = getActiveTenantSubscription(user);
  return !!(subscription?.plan_name || user.tenant_subscription_plan);
};

const DEFAULT_MODULE_ACCESS_DENIED_MESSAGE =
  "You don't have access to this module, please contact the support team for further information.";

const SUBSCRIPTION_MODULE_ACCESS_DENIED_MESSAGE =
  "This feature is not available on your subscription plan. Please contact support for further info.";

const SUBSCRIPTION_PENDING_ENABLEMENT_MESSAGE =
  "This feature is included in your subscription but has not been enabled for your account yet and is pending approval from the admin. Please contact support for further information.";

const DISTRIBUTION_PENDING_ENABLEMENT_MESSAGE =
  "Distribution access is currently pending approval from the admin. Please contact support for further information.";

const MODULE_PENDING_ENABLEMENT_MESSAGES = {
  distribution:
    "Distribution module access is currently pending approval from the admin. Please contact support for further information.",
  artists:
    "The Artists module has not been enabled for your account yet and is pending approval from the admin. Please contact support for further information.",
  music_link:
    "Music Links have not been enabled for your account yet and are pending approval from the admin. Please contact support for further information.",
  link_in_bio:
    "Link Landing Pages have not been enabled for your account yet and are pending approval from the admin. Please contact support for further information.",
  trends:
    "Trends has not been enabled for your account yet and is pending approval from the admin. Please contact support for further information.",
};

const getPendingEnablementMessage = (module) => {
  if (module && MODULE_PENDING_ENABLEMENT_MESSAGES[module]) {
    return MODULE_PENDING_ENABLEMENT_MESSAGES[module];
  }

  return SUBSCRIPTION_PENDING_ENABLEMENT_MESSAGE;
};

const SUBSCRIPTION_PLAN_MODULE_ENTITLEMENTS = {
  distribution: (plan) => (Number(plan.song_limit) || 0) > 0,
  artists: (plan) => (Number(plan.artist_limit) || 0) > 0,
  music_link: (plan) => !!plan.music_link_enabled,
  link_in_bio: (plan) => !!plan.link_landing_page_enabled,
  trends: (plan) => (Number(plan.song_limit) || 0) > 0,
};

const hasSubscriptionPlanEntitlementData = (subscription, module) => {
  if (!subscription || !SUBSCRIPTION_PLAN_MODULE_ENTITLEMENTS[module]) {
    return false;
  }

  switch (module) {
    case "distribution":
    case "trends":
      return subscription.song_limit != null && subscription.song_limit !== "";
    case "artists":
      return subscription.artist_limit != null && subscription.artist_limit !== "";
    case "music_link":
      return subscription.music_link_enabled != null;
    case "link_in_bio":
      return subscription.link_landing_page_enabled != null;
    default:
      return false;
  }
};

export const isModuleIncludedInSubscriptionPlan = (subscription, module) => {
  const checker = SUBSCRIPTION_PLAN_MODULE_ENTITLEMENTS[module];
  if (!checker) {
    return undefined;
  }

  if (!hasSubscriptionPlanEntitlementData(subscription, module)) {
    return undefined;
  }

  return checker(subscription);
};

export const userHasActiveTenantSubscription = (user) => {
  const subscription = getActiveTenantSubscription(user);
  if (!subscription?.plan_name) {
    return false;
  }

  const status = String(subscription.status || "")
    .toLowerCase()
    .replace(/\s+/g, "_");

  if (!status) {
    return true;
  }

  return status === "active" || status === "trialing";
};

export const getModuleAccessDeniedMessage = (
  user,
  { fallbackMessage = DEFAULT_MODULE_ACCESS_DENIED_MESSAGE, module } = {}
) => {
  if (!userHasTenantSubscriptionPlan(user)) {
    return fallbackMessage;
  }

  const subscription = getActiveTenantSubscription(user);
  const planIncludesModule = module
    ? isModuleIncludedInSubscriptionPlan(subscription, module)
    : undefined;

  if (planIncludesModule === false) {
    if (
      module === "distribution"
      && userHasActiveTenantSubscription(user)
      && !user.distribution
    ) {
      return getPendingEnablementMessage(module);
    }
    return SUBSCRIPTION_MODULE_ACCESS_DENIED_MESSAGE;
  }

  if (userHasActiveTenantSubscription(user)) {
    return getPendingEnablementMessage(module);
  }

  return DISTRIBUTION_PENDING_ENABLEMENT_MESSAGE;
};

export const isPlanDowngrade = (currentPlan, targetPlan) => {
  if (!currentPlan || !targetPlan || currentPlan.id === targetPlan.id) {
    return false;
  }

  return (targetPlan.order ?? 0) < (currentPlan.order ?? 0);
};

export const getPlanCardCta = (plan, { currentPlan, hasActiveSubscription } = {}) => {
  if (plan.is_current) {
    return { label: "Current Plan", disabled: true, locked: false };
  }

  if (!hasActiveSubscription) {
    return { label: "Subscribe", disabled: false, locked: false };
  }

  if (isPlanDowngrade(currentPlan, plan)) {
    return { label: "Change Plan", disabled: true, locked: true };
  }

  return { label: "Change Plan", disabled: false, locked: false };
};

export const buildBillingPortalUrl = (billingPortalUrl, subscriptionId, intent = "manage") => {
  if (!billingPortalUrl) {
    return null;
  }

  let baseUrl = billingPortalUrl.trim();

  try {
    const url = new URL(baseUrl);
    url.searchParams.delete("subscription_id");
    const cleanedSearch = url.searchParams.toString();
    baseUrl = `${url.origin}${url.pathname}${cleanedSearch ? `?${cleanedSearch}` : ""}`;
  } catch {
    baseUrl = baseUrl
      .replace(/([?&])subscription_id=[^&]*/g, "")
      .replace(/\?&/, "?")
      .replace(/\?$/, "");
  }

  baseUrl = baseUrl.replace(/\/subscriptions\/sub_[^/]+\/update\/?$/i, "");
  baseUrl = baseUrl.replace(/\/+$/, "");

  const stripeSubscriptionId = String(subscriptionId || "").trim();
  if (!stripeSubscriptionId.startsWith("sub_") || intent !== "update") {
    return baseUrl;
  }

  return `${baseUrl}/subscriptions/${stripeSubscriptionId}/update`;
};

export const subscriptionIsAutoRenewing = (subscription) => {
  if (!subscription) {
    return null;
  }

  const status = String(subscription.status || "").toLowerCase().replace(/\s+/g, "_");
  const canceledAt = subscription.canceled_at ?? subscription.cancelled_at;
  const cancelAtPeriodEnd = subscription.cancel_at_period_end === true;
  const hasScheduledCancel = subscription.cancel_at != null && subscription.cancel_at !== "";

  if (
    status === "canceled"
    || status === "cancelled"
    || canceledAt
    || cancelAtPeriodEnd
    || hasScheduledCancel
  ) {
    return false;
  }

  if (subscription.auto_renews === false) {
    return false;
  }

  if (subscription.auto_renews === true) {
    return true;
  }

  return status === "active" || status === "trialing";
};

export const getSubscriptionRenewalLabel = (subscription) => {
  if (!subscription) {
    return "";
  }

  const autoRenews = subscriptionIsAutoRenewing(subscription);
  const periodEnd = formatSubscriptionDate(getSubscriptionPeriodEnd(subscription));
  const canceledAt = formatSubscriptionDate(
    subscription.canceled_at ?? subscription.cancelled_at
  );
  const cancelAt = formatSubscriptionDate(subscription.cancel_at);
  const status = String(subscription.status || "").toLowerCase();

  if (autoRenews === true) {
    return periodEnd ? `Auto-renews on ${periodEnd}` : "Auto-renews";
  }

  if (autoRenews !== false) {
    return "";
  }

  if (subscription.cancel_at_period_end && periodEnd) {
    return `Cancels on ${periodEnd}`;
  }

  if (canceledAt || status === "canceled" || status === "cancelled") {
    return canceledAt ? `Canceled on ${canceledAt}` : "Canceled";
  }

  if (cancelAt) {
    return `Scheduled to cancel on ${cancelAt}`;
  }

  if (periodEnd) {
    return `Ends on ${periodEnd}`;
  }

  return "Does not auto-renew";
};

export const enrichSubscriptionDetails = (subscription, plans = []) => {
  if (!subscription?.plan_name) {
    return null;
  }

  const enriched = { ...subscription };
  const matchedPlan = plans.find(
    (plan) =>
      (subscription.product_id && plan.id === subscription.product_id) ||
      plan.name === subscription.plan_name ||
      plan.is_current
  );

  if (matchedPlan) {
    enriched.price_display = enriched.price_display || matchedPlan.price_display;
    enriched.interval = enriched.interval || matchedPlan.interval;
    enriched.song_limit = enriched.song_limit ?? matchedPlan.song_limit;
    enriched.artist_limit = enriched.artist_limit ?? matchedPlan.artist_limit;
    enriched.music_link_limit = enriched.music_link_limit ?? matchedPlan.music_link_limit;
    enriched.link_landing_page_limit =
      enriched.link_landing_page_limit ?? matchedPlan.link_landing_page_limit;
    enriched.music_link_enabled =
      enriched.music_link_enabled ?? matchedPlan.music_link_enabled;
    enriched.link_landing_page_enabled =
      enriched.link_landing_page_enabled ?? matchedPlan.link_landing_page_enabled;
    enriched.deal_percent = enriched.deal_percent ?? matchedPlan.deal_percent;

    if (!enriched.amount && matchedPlan.amount) {
      enriched.amount = matchedPlan.amount;
    }
    if (!enriched.currency && matchedPlan.currency) {
      enriched.currency = matchedPlan.currency;
    }
  }

  if (!enriched.price_display && enriched.amount) {
    enriched.price_display = formatPlanPriceDisplay(
      enriched.amount,
      enriched.currency,
      enriched.interval
    );
  }

  return enriched;
};

export const buildSubscriptionBillingRows = (subscription) => {
  if (!subscription) {
    return [];
  }

  const rows = [];
  const autoRenews = subscriptionIsAutoRenewing(subscription);
  const periodStart = formatSubscriptionDate(getSubscriptionPeriodStart(subscription));
  const periodEnd = formatSubscriptionDate(getSubscriptionPeriodEnd(subscription));

  if (subscription.status) {
    rows.push({
      label: "Status",
      value: formatSubscriptionStatus(subscription.status),
    });
  }

  if (subscription.price_display) {
    rows.push({ label: "Price", value: subscription.price_display });
  } else if (subscription.amount) {
    rows.push({
      label: "Price",
      value: formatPlanPriceDisplay(
        subscription.amount,
        subscription.currency,
        subscription.interval
      ),
    });
  }

  if (subscription.interval) {
    rows.push({
      label: "Billing cycle",
      value: subscription.interval === "year" ? "Yearly" : "Monthly",
    });
  }

  if (periodStart) {
    rows.push({ label: "Period started", value: periodStart });
  }

  if (periodEnd) {
    rows.push({
      label: autoRenews === false ? "Subscription ends" : "Current period ends",
      value: periodEnd,
    });
  }

  const renewalLabel = getSubscriptionRenewalLabel(subscription);
  if (renewalLabel) {
    rows.push({ label: "Renewal", value: renewalLabel });
  }

  if (subscription.canceled_at || subscription.cancelled_at) {
    rows.push({
      label: "Canceled on",
      value: formatSubscriptionDate(subscription.canceled_at ?? subscription.cancelled_at),
    });
  }

  return rows;
};

export const buildSubscriptionSummaryFields = (subscription) => {
  if (!subscription) {
    return [];
  }

  const fields = [];
  const periodEnd = formatSubscriptionDate(getSubscriptionPeriodEnd(subscription));
  const periodStart = formatSubscriptionDate(getSubscriptionPeriodStart(subscription));

  if (subscription.interval) {
    fields.push({
      label: "Billing cycle",
      value: subscription.interval === "year" ? "Yearly" : "Monthly",
    });
  }

  if (periodStart) {
    fields.push({ label: "Period started", value: periodStart });
  }

  if (periodEnd) {
    fields.push({ label: "Period ends", value: periodEnd });
  }

  const renewalLabel = getSubscriptionRenewalLabel(subscription);
  if (renewalLabel) {
    fields.push({ label: "Renewal", value: renewalLabel });
  }

  return fields;
};

export const getSubscriptionCancelMessage = (subscription) => {
  if (!subscription || subscriptionIsAutoRenewing(subscription) !== false) {
    return "";
  }

  const periodEnd = formatSubscriptionDate(getSubscriptionPeriodEnd(subscription));
  const canceledAt = formatSubscriptionDate(
    subscription.canceled_at ?? subscription.cancelled_at
  );
  const cancelAt = formatSubscriptionDate(subscription.cancel_at);

  if (subscription.cancel_at_period_end && periodEnd) {
    return `Cancels on ${periodEnd}. Access continues until then.`;
  }

  if (canceledAt) {
    return `Canceled on ${canceledAt}.`;
  }

  if (cancelAt) {
    return `Scheduled to cancel on ${cancelAt}.`;
  }

  if (periodEnd) {
    return `Ends on ${periodEnd}.`;
  }

  return "Your subscription will not renew.";
};

export const getSubscriptionRenewalMessage = (subscription) => {
  if (!getSubscriptionPeriodEnd(subscription) || subscriptionIsAutoRenewing(subscription) === false) {
    return "";
  }

  const formattedDate = formatSubscriptionDate(getSubscriptionPeriodEnd(subscription));
  return formattedDate ? `Renews on ${formattedDate}` : "";
};

export const buildSubscriptionEntitlements = (subscription) => (
  buildPlanEntitlements(subscription || {})
);

const toNonNegativeNumber = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return Math.max(0, parsed);
};

const resolveAllowanceTotal = (...candidates) => {
  for (const candidate of candidates) {
    const parsed = toNonNegativeNumber(candidate);
    if (parsed != null && parsed > 0) {
      return parsed;
    }
  }
  return null;
};

const buildUsageAllowanceItem = ({
  key,
  label,
  total,
  remaining,
  unlimited = false,
  enabled = true,
}) => {
  if (!enabled) {
    return null;
  }

  if (unlimited) {
    return {
      key,
      label,
      total: null,
      remaining: null,
      used: null,
      unlimited: true,
    };
  }

  const resolvedTotal = toNonNegativeNumber(total);
  const resolvedRemaining = toNonNegativeNumber(remaining);

  if (resolvedTotal == null && resolvedRemaining == null) {
    return null;
  }

  const safeTotal = resolvedTotal ?? resolvedRemaining ?? 0;
  const safeRemaining = resolvedRemaining ?? safeTotal;
  const used = Math.max(0, safeTotal - safeRemaining);

  return {
    key,
    label,
    total: safeTotal,
    remaining: safeRemaining,
    used,
    unlimited: false,
  };
};

export const buildSubscriptionAllowanceItems = (user = {}, subscription = null) => {
  if (!user) {
    return [];
  }

  const plan = subscription || {};
  const items = [];

  const songsItem = buildUsageAllowanceItem({
    key: "songs",
    label: "Songs",
    total: resolveAllowanceTotal(plan.song_limit, user.track_allowance),
    remaining: user.total_allowed_track_amount,
    unlimited: !!user.unlimited_track_amount,
    enabled:
      !!user.unlimited_track_amount
      || resolveAllowanceTotal(plan.song_limit, user.track_allowance) != null
      || user.total_allowed_track_amount != null,
  });
  if (songsItem) {
    items.push(songsItem);
  }

  const artistTotal = resolveAllowanceTotal(plan.artist_limit);
  const artistRemaining = toNonNegativeNumber(user.artist_allowance);
  const artistsItem = buildUsageAllowanceItem({
    key: "artists",
    label: "Artists",
    total: artistTotal ?? artistRemaining,
    remaining: artistRemaining,
    enabled: artistTotal != null || artistRemaining != null || !!user.artists,
  });
  if (artistsItem) {
    items.push(artistsItem);
  }

  const musicLinkEnabled = plan.music_link_enabled !== false && !!user.music_link;
  const musicLinkTotal = resolveAllowanceTotal(plan.music_link_limit);
  const musicLinkRemaining = toNonNegativeNumber(user.music_link_allowance);
  const musicLinksItem = buildUsageAllowanceItem({
    key: "music_links",
    label: "Music links",
    total: musicLinkTotal ?? musicLinkRemaining,
    remaining: musicLinkRemaining,
    enabled:
      musicLinkEnabled
      && (musicLinkTotal != null || musicLinkRemaining != null),
  });
  if (musicLinksItem) {
    items.push(musicLinksItem);
  }

  const landingPageEnabled = plan.link_landing_page_enabled !== false && !!user.link_in_bio;
  const landingPageTotal = resolveAllowanceTotal(plan.link_landing_page_limit);
  const landingPageRemaining = toNonNegativeNumber(user.link_landing_page_allowance);
  const landingPagesItem = buildUsageAllowanceItem({
    key: "landing_pages",
    label: "Link landing pages",
    total: landingPageTotal ?? landingPageRemaining,
    remaining: landingPageRemaining,
    enabled:
      landingPageEnabled
      && (landingPageTotal != null || landingPageRemaining != null),
  });
  if (landingPagesItem) {
    items.push(landingPagesItem);
  }

  return items;
};

export const formatAllowanceUsage = (item) => {
  if (!item) {
    return "";
  }

  if (item.unlimited) {
    return `${item.label}: Unlimited`;
  }

  if (item.total == null && item.remaining == null) {
    return "";
  }

  const used = item.used ?? 0;
  const total = item.total ?? item.remaining ?? 0;
  return `${item.label}: ${used}/${total}`;
};

export const formatAllowanceSummary = (items = [], { maxItems = 3 } = {}) => {
  const parts = items
    .map((item) => formatAllowanceUsage(item))
    .filter(Boolean)
    .slice(0, maxItems);

  return parts.join(" · ");
};

export const getAllowanceUsagePercent = (item) => {
  if (!item || item.unlimited || !item.total) {
    return 0;
  }

  const used = item.used ?? 0;
  return Math.min(100, Math.round((used / item.total) * 100));
};

export const normalizePlanInterval = (plan) => {
  const interval = String(plan?.interval || "").toLowerCase();
  if (interval === "year" || interval === "yearly") {
    return "year";
  }
  if (interval === "month" || interval === "monthly") {
    return "month";
  }

  const priceDisplay = String(plan?.price_display || "");
  if (/\/\s*year\s*$/i.test(priceDisplay)) {
    return "year";
  }

  return "month";
};

export const getPlansBillingIntervals = (plans = []) => {
  const intervals = new Set(plans.map((plan) => normalizePlanInterval(plan)));
  return ["month", "year"].filter((interval) => intervals.has(interval));
};

export const getPlansForBillingInterval = (plans = [], interval = "month") => (
  plans.filter((plan) => normalizePlanInterval(plan) === interval)
);

export const formatPlanPriceDisplay = (amount, currency, interval) => {
  const num = parseFloat(amount);
  if (amount === "" || amount === null || amount === undefined || Number.isNaN(num)) {
    return "—";
  }

  const currencyCode = (currency || "eur").toLowerCase();
  const intervalLabel = interval === "year" ? "/ year" : "/ month";

  try {
    const formatted = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currencyCode,
    }).format(num);
    return `${formatted} ${intervalLabel}`;
  } catch {
    return `${num.toFixed(2)} ${currencyCode.toUpperCase()} ${intervalLabel}`;
  }
};

export const splitPriceDisplay = (priceDisplay) => {
  if (!priceDisplay || priceDisplay === "—") {
    return { amount: "—", interval: "" };
  }

  const match = priceDisplay.match(/^(.+?)\s*\/\s*(month|year)\s*$/i);
  if (match) {
    return {
      amount: match[1].trim(),
      interval: `per ${match[2].toLowerCase()}`,
    };
  }

  return { amount: priceDisplay, interval: "" };
};

export const buildPlanLineItems = (features = [], entitlements = []) => {
  const seen = new Set();
  const items = [];

  [...features, ...entitlements].forEach((item) => {
    const label = String(item || "").trim();
    const key = label.toLowerCase();
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    items.push(label);
  });

  return items;
};

export const PLAN_DESCRIPTION_MAX_LENGTH = 90;

export const truncatePlanDescription = (
  description,
  maxLength = PLAN_DESCRIPTION_MAX_LENGTH
) => {
  const text = String(description || "").trim().replace(/\s+/g, " ");
  if (!text) {
    return "";
  }

  if (text.length <= maxLength) {
    return text;
  }

  const truncated = text.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(" ");
  const cut = lastSpace > maxLength * 0.6 ? truncated.slice(0, lastSpace) : truncated;

  return `${cut.trim()}…`;
};

export const draftToPreviewPlan = (draftProduct, tenantCurrency) => ({
  name: draftProduct.name || "Plan name",
  description: draftProduct.description || "",
  price_display: formatPlanPriceDisplay(
    draftProduct.amount,
    tenantCurrency,
    draftProduct.interval
  ),
  features: draftProduct.features || [],
  deal_percent: draftProduct.deal_percent || 0,
  song_limit: Number(draftProduct.song_limit) || 0,
  artist_limit: Number(draftProduct.artist_limit) || 0,
  music_link_limit: Number(draftProduct.music_link_limit) || 0,
  link_landing_page_limit: Number(draftProduct.link_landing_page_limit) || 0,
  music_link_enabled: !!draftProduct.music_link_enabled,
  link_landing_page_enabled: !!draftProduct.link_landing_page_enabled,
});
